import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "../types";
import { useIsUserDev } from "../../../../../../hooks/useIsUserDev";

interface ProviderHeaderProps {
  themeClasses: ThemeClasses;
  selectedStatus: string;
  searchQuery: string;
  selectedView: "grid" | "list";
  onStatusChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onViewChange: (view: "grid" | "list") => void;
  onAddProvider: () => void;
}

export default function ProviderHeader({
  themeClasses,
  selectedStatus,
  searchQuery,
  selectedView,
  onStatusChange,
  onSearchChange,
  onViewChange,
  onAddProvider,
}: ProviderHeaderProps) {
  const { t } = useTranslation("resellerAdmin");
  const { isUserDev } = useIsUserDev();

  return (
    <div className={`${themeClasses.bg} border-b ${themeClasses.border} p-4 sm:p-6`}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}>
            <i className="ri-cloud-line mr-2"></i>
            {t("providers.title")}
          </h1>
          <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
            {t("providers.subtitle")}
          </p>
        </div>
        {isUserDev && (
          <button
            onClick={onAddProvider}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-gray-900 rounded-xl font-medium hover:bg-primary-600 transition-all shadow-lg hover:shadow-xl text-sm"
          >
            <i className="ri-add-line text-lg"></i>
            {t("providers.addProvider")}
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4">
        <div className="relative flex-1">
          <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
          <input
            type="text"
            placeholder={t("providers.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 rounded-xl border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50`}
          />
        </div>

        <select
          value={selectedStatus}
          onChange={(e) => onStatusChange(e.target.value)}
          className={`px-4 py-2.5 rounded-xl border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50`}
        >
          <option value="all">{t("providers.allStatuses")}</option>
          <option value="true">{t("providers.active")}</option>
          <option value="false">{t("providers.inactive")}</option>
        </select>

        <div className={`flex rounded-xl border ${themeClasses.border} overflow-hidden`}>
          <button
            onClick={() => onViewChange("grid")}
            className={`p-2.5 ${
              selectedView === "grid"
                ? "bg-primary-500 text-gray-900"
                : `${themeClasses.bg} ${themeClasses.buttonText}`
            }`}
          >
            <i className="ri-grid-fill text-lg"></i>
          </button>
          <button
            onClick={() => onViewChange("list")}
            className={`p-2.5 ${
              selectedView === "list"
                ? "bg-primary-500 text-gray-900"
                : `${themeClasses.bg} ${themeClasses.buttonText}`
            }`}
          >
            <i className="ri-list-unordered text-lg"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
